import React, { Component } from 'react';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import _ from 'lodash';
import { connect } from 'react-redux';
import { compose, lifecycle } from 'recompose';
import {
  Grid,
  Row,
  Col,
  Button,
  Modal,
  ControlLabel,
  FormGroup,
  Label,
  Glyphicon,
} from 'react-bootstrap';

import { SubmissionList } from '../../components/SubmissionList';
import ImageViewWithData from '../../components/ImageView';
import CrewTimeEntryEditor from '../../containers/CrewTimeEntryEditor';
import TicketAttachmentEditor from './TicketAttachmentEditor';
import MultipageTypeLayout from './MultipageTypeLayout';
import ConfirmSplitModal from './ConfirmSplitModal';
import {
  querySelector,
  parseErrors,
} from '../../utils';
import TypeLayout from './TypeLayout';
import TicketEntryLayout from '../TicketEntryLayout/TicketEntryLayout';
import FieldDistricts from '../../components/FieldDistricts/index';

import { getUserDefaultDistrictId } from '../../features/authentication/selectors';

import { routerProps } from '../../propTypes';
import { getSubmissionEmailAddress, getSubmissionPhoneNumber } from '../../features/configuration/selectors';

import { setDistrictId } from './actions';
import { getDistrictId } from './selector';
/**
* IDEA: Align proptypes and fragments to achieve good parity between query data
* and type safety within component properties. This should also make updating
* queries and prop types much simplier, safer, and faster.
*
* More:
* >> https://www.apollographql.com/docs/react/features/fragments.html
* >> https://www.learnapollo.com/tutorial-react/react-04
*/
/* eslint-disable no-unused-vars, react/no-unused-prop-types */
const simpleUserType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  firstname: PropTypes.string.isRequired,
  lastname: PropTypes.string.isRequired,
  cell_number: PropTypes.string.isRequired,
});

const submissionItemType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  // NOTE: When it's unapproved, submittedBy is null coming across the wire, removing required
  submittedBy: simpleUserType,
  submission_type: PropTypes.string.isRequired,
});

const contactMethodPropTypes = exact({
  submissionEmailAddress: PropTypes.string.isRequired,
  submissionPhoneNumber: PropTypes.string.isRequired,
});

const propTypes = exact({
  items: PropTypes.arrayOf(submissionItemType),

  data: PropTypes.object,
  completeItem: PropTypes.func,
  rejectItem: PropTypes.func,
  addManualItem: PropTypes.func,
  changeItemType: PropTypes.func,
  splitSubmission: PropTypes.func,

  districtId: PropTypes.number,
  setDistrictIdAction: PropTypes.func.isRequired,

  defaultUserDistrictId: PropTypes.number.isRequired,
  ...routerProps,
  ...contactMethodPropTypes,
});


export class TimesheetSubmissionProcessor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedItem: null,
      errorMessage: null,
      showConfirmModal: false,
      splitDisabled: false,
      selectedIndex: 0,
    };

    this.handleSelect = this.handleSelect.bind(this);
    this.handleReject = this.handleReject.bind(this);
    this.handleComplete = this.handleComplete.bind(this);
    this.handleAddManualItem = this.handleAddManualItem.bind(this);
    this.handleTypeChange = this.handleTypeChange.bind(this);
    this.handleClearError = this.handleClearError.bind(this);
    this.handleFieldDistrictSelect = this.handleFieldDistrictSelect.bind(this);

    this.closeSplitModal = this.closeSplitModal.bind(this);
    this.openSplitModal = this.openSplitModal.bind(this);
    this.handleSplit = this.handleSplit.bind(this);
    this.handleSplitConfirmed = this.handleSplitConfirmed.bind(this);
    this.selectIndex = this.selectIndex.bind(this);
  }

  closeSplitModal() {
    this.setState({
      showConfirmModal: false,
    });
  }

  openSplitModal() {
    this.setState({
      showConfirmModal: true,
    });
  }

  async handleSplitConfirmed() {
    try {
      this.setState({
        splitDisabled: true,
      });
      await this.handleSplit();
    } catch (e) {
      const errorMessage = parseErrors(e);
      this.setState({ errorMessage });
    } finally {
      this.setState({
        splitDisabled: false,
      });
      this.closeSplitModal();
    }
  }

  async handleSplit() {
    const { selectedItem } = this.state;
    try {
      const variables = {
        input: {
          submission_item_id: selectedItem.id,
        },
      };
      const nextItem = this.nextItemId(this.props.items, selectedItem);
      await this.props.splitSubmission({ variables });
      await this.props.data.refetch();
      this.setState({
        selectedItem: nextItem,
        errorMessage: null,
        selectedIndex: 0,
      });
    } catch (e) {
      const errorMessage = parseErrors(e);
      this.setState({ errorMessage });
    }
  }

  handleClearError() {
    const errorMessage = null;
    this.setState({ errorMessage });
  }

  selectIndex(selectedIndex) {
    const { fileCount } = this.state.selectedItem.document;

    if (selectedIndex >= 0 && selectedIndex < fileCount) {
      this.setState({
        selectedIndex,
      });
    }
  }

  nextItemId(itemCollection, currentItem) {
    const currentIndex = _.findIndex(itemCollection, { id: currentItem.id });
    const itemsLength = itemCollection.length;
    const isLast = (currentIndex + 1) === itemsLength;

    if (isLast) {
      if (itemsLength === 1) {
        return null;
      }
      const prevItem = this.props.items[currentIndex - 1];
      return prevItem;
    }
    const nextItem = this.props.items[currentIndex + 1];
    return nextItem;
  }

  handleSelect(item) {
    if (!_.find(this.props.items, { id: item.id })) {
      this.setState({ errorMessage: 'Unexpected error' });
      return;
    }
    this.setState({ selectedItem: item, selectedIndex: 0 });
  }

  updateSelectedItem(itemId) {
    // returns array of elements
    const filteredItem = _.filter(this.props.items, { id: itemId });

    if (!filteredItem) {
      this.setState({ errorMessage: 'Error changing item type' });
      return;
    }
    const selectedItem = filteredItem[0];
    this.setState({ selectedItem, selectedIndex: 0 });
  }

  handleFieldDistrictSelect(e) {
    this.setState({ selectedIndex: 0 });


    this.props.setDistrictIdAction(e.target.value);
    const variables = {
      debugSubmissionState: 'unprocessed',
      districtId: e.target.value,
    };
    this.props.data.refetch(variables);
  }

  async handleTypeChange(stringType) {
    const itemId = this.state.selectedItem.id;
    try {
      const variables = {
        input: {
          submission_item_id: itemId,
          submission_type: stringType,
        },
      };
      await this.props.changeItemType({ variables });
      await this.props.data.refetch();
      this.updateSelectedItem(itemId);
    } catch (e) {
      const errorMessage = parseErrors(e);
      this.setState({ errorMessage });
    }
  }

  async handleComplete(item) {
    try {
      const variables = {
        input: {
          submission_item_id: item.id,
        },
      };
      const nextItem = this.nextItemId(this.props.items, item);
      await this.props.completeItem({ variables });
      await this.props.data.refetch();
      this.setState({ selectedItem: nextItem, selectedIndex: 0 });
    } catch (e) {
      const errorMessage = parseErrors(e);
      this.setState({ errorMessage });
    }
  }

  async handleReject(item, reason, notifySubmitter, usersToNotify) {
    /* Update the mutation to take in a list of users */
    try {
      const variables = {
        input: {
          state_change_reason: reason,
          submission_item_id: item.id,
          notify_submitter: notifySubmitter,
          additional_user_ids_to_notify: usersToNotify,
        },
      };
      const nextItem = this.nextItemId(this.props.items, item);
      await this.props.rejectItem({ variables });
      await this.props.data.refetch();
      this.setState({
        selectedItem: nextItem,
        errorMessage: null,
        selectedIndex: 0,
      });
    } catch (e) {
      const errorMessage = parseErrors(e);
      this.setState({ errorMessage });
    }
  }

  async handleAddManualItem() {
    try {
      const variables = {
        input: {},
      };

      const result = await this.props.addManualItem({ variables });
      const selectedItem = result.data.submission_item_add_manual;
      await this.props.data.refetch();
      this.setState({ selectedItem, selectedIndex: 0 });
    } catch (e) {
      const errorMessage = parseErrors(e);
      this.setState({ errorMessage });
    }
  }

  debugSetSubmissionSet(debugSubmissionState) {
    const variables = {
      debugSubmissionState,
    };
    this.props.data.refetch(variables);
  }

  renderImage() {
    if (!this.state.selectedItem) {
      return (
        <div className="col-md-5 office-height">please select</div>
      );
    }

    const { selectedIndex } = this.state;

    return (
      <React.Fragment>
        <Col md={5} className="office-height office-border-right">
          <ImageViewWithData
            openSplitModal={this.openSplitModal}
            selectedIndex={selectedIndex}
            selectIndex={this.selectIndex}
            selectedId={this.state.selectedItem.id}
            onReject={this.handleReject}
            handleTypeChange={this.handleTypeChange}
            onTypeChange={(data, stringType) => { return this.handleTypeChange(data, stringType); }}
          />
        </Col>
        {this.renderEditor()}
      </React.Fragment>
    );
  }

  renderEditor() {
    const {
      submission_type: submissionType,
      rawUrl,
      submission,
    } = this.state.selectedItem;

    const { multipage } = this.state.selectedItem.document;
    const { selectedIndex } = this.state;

    const ticketNumbers = submission.ticket_numbers.map((item) => {
      return item.number;
    });

    if (submissionType === 'timesheet') {
      return (
        <Col md={5} className=" office-height office-border-right">
          <CrewTimeEntryEditor
            itemId={this.state.selectedItem.id}
            selectedItem={this.state.selectedItem}
            onComplete={this.handleComplete}
          />
        </Col>
      );
    }
    if (submissionType === 'ticket') {
      return (
        <Col md={5} className="office-height office-border-right">
          <TicketEntryLayout
            selectedId={this.state.selectedItem.id}
            onComplete={this.handleComplete}
          />
        </Col>
      );
    } if (submissionType === 'ticket_attachment') {
      return (
        <Col md={5} className="office-height office-border-right">
          <TicketAttachmentEditor
            ticketNumbers={ticketNumbers}
            item={this.state.selectedItem}
            onComplete={this.handleComplete}
          />
        </Col>
      );
    } if (multipage) {
      return (
        <Col md={5} className="office-height office-border-right">
          <MultipageTypeLayout
            selectedId={this.state.selectedItem.id}
            selectIndex={this.selectIndex}
            selectedIndex={selectedIndex}
            showConfirmModal={this.openSplitModal}
            onTypeChange={(data, stringType) => { return this.handleTypeChange(data, stringType); }}
          />
        </Col>
      );
    }


    return (
      <Col md={5} className="office-height office-border-right">
        <TypeLayout
          onTypeChange={(data, stringType) => { return this.handleTypeChange(data, stringType); }}
        />
      </Col>
    );
  }

  render() {
    const { errorMessage, showConfirmModal, splitDisabled } = this.state;
    const { submissionEmailAddress, submissionPhoneNumber, districtId } = this.props;

    return (
      <Grid fluid>
        <Row>
          <Col md={4}>
            <h3 className="pull-left">Inbox</h3>
          </Col>
          <Col md={8}>
            <h3 style={{ display: 'inline-block', paddingRight: '5%' }}>
              <Label>
                <Glyphicon glyph="envelope" style={{ verticalAlign: 'middle' }} />
                <Label style={{ fontSize: '15px' }}>
                  {submissionEmailAddress}
                </Label>
              </Label>
            </h3>
            <h3 style={{ display: 'inline-block' }}>
              <Label>
                <Glyphicon glyph="phone" style={{ verticalAlign: 'middle' }} />
                <Label style={{ fontSize: '15px' }}>
                  {submissionPhoneNumber }
                </Label>
              </Label>
            </h3>
            <Button
              name="reject"
              bsStyle="primary"
              className="pull-right"
              onClick={() => { return this.handleAddManualItem(); }}
            >
              Add Manual Submission
            </Button>
          </Col>
        </Row>
        <hr className="app-remove-top-margin" />
        <Row>
          <Col md={2}>
            <FormGroup>
              <ControlLabel className="pull-left control-label">
                Field District:
              </ControlLabel>
              <FieldDistricts
                districtId={districtId}
                onSelectDistrict={this.handleFieldDistrictSelect}
              />
            </FormGroup>
            <SubmissionList
              onSelect={this.handleSelect}
              items={this.props.items}
              selectedItem={this.state.selectedItem}
            />
          </Col>
          {this.renderImage()}
          <Modal
            show={!!errorMessage}
            onHide={this.handleClearError}
            container={this}
            backdrop={false}
            aria-labelledby="contained-modal-title"
          >
            <Modal.Header closeButton>
              <Modal.Title id="contained-modal-title">Error</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {errorMessage}
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={this.handleClearError}>Close</Button>
            </Modal.Footer>
          </Modal>
          <ConfirmSplitModal
            onHide={this.closeSplitModal}
            showModal={showConfirmModal}
            confirm={this.handleSplitConfirmed}
            disable={splitDisabled}
          />
        </Row>
      </Grid>
    );
  }
}

TimesheetSubmissionProcessor.propTypes = propTypes;

const SimpleDateFragment = gql`
  fragment SimpleDate on DateTime {
    iso8601
    date
    time
    zone
  }
`;

const SimpleUserFragment = gql`
  fragment SimpleUser on User {
    id
    firstname
    lastname
    cell_number
  }
`;

const SimpleSubmissionItemFragment = gql`
  fragment SimpleSubmissionItem on SubmissionItem {
    id
    state
    submission_type
    document {
      id
      multipage
      fileCount: file_count
    }
    submission {
      id
      ticket_numbers {
        number
      }
      submitted_at {
        ...SimpleDate
      }
      submitted_by {
        ...SimpleUser
      }
    }
  }
  ${SimpleUserFragment}
  ${SimpleDateFragment}
`;

const UnprocessedSubmissionItemsQuery = gql`
  query UnprocessedSubmissionItems($debugSubmissionState: SubmissionItemStateTypes, $districtId: Int) {
    all_submission_items(state: $debugSubmissionState, district_id: $districtId ) {
      edges {
        node {
          ...SimpleSubmissionItem
        }
      }
    }
  }
  ${SimpleSubmissionItemFragment}
`;

const CompleteSubmissionItemMutation = gql`
  mutation CompleteSubmissionItem($input: SubmissionItemCompleteInput!) {
    submission_item_complete(input: $input) {
      ...SimpleSubmissionItem
    }
  }
  ${SimpleSubmissionItemFragment}
`;

const RejectSubmissionItemMutation = gql`
  mutation RejectSubmissionItem($input: SubmissionItemRejectInput!) {
    submission_item_reject(input: $input) {
      ...SimpleSubmissionItem
    }
  }
  ${SimpleSubmissionItemFragment}
`;

const AddManualSubmissionItemMutation = gql`
  mutation AddManualSubmissionItem($input: SubmissionItemAddUnknownInput!) {
    submission_item_add_unknown(input: $input) {
      ...SimpleSubmissionItem
    }
  }
    ${SimpleSubmissionItemFragment}
`;

const ChangeSubmissionItemTypeMutation = gql`
  mutation ChangeSubmissionItemType($input: SubmissionItemChangeSubmissionTypeInput!) {
    submission_item_change_submission_type(input: $input) {
      ...SimpleSubmissionItem
    }
  }
    ${SimpleSubmissionItemFragment}
`;

const SplitSubmissionItemMutation = gql`
  mutation SubmissionItemSplit($input: SubmissionItemSplitInput!) {
    submission_item_split(input: $input)
  }
 `;

export const marshallData = ({ data, ownProps }) => {
  const items = querySelector(data, '$.all_submission_items.edges[*].node', []);
  return {
    ...ownProps,
    data,
    items,
  };
};

const withData = graphql(UnprocessedSubmissionItemsQuery, {
  props: marshallData,
  options: (props) => {
    return {
      fetchPolicy: 'network-only',
      pollInterval: 1000 * 60 * 2,
      variables: {
        debugSubmissionState: 'unprocessed',
        districtId: props.districtId,
      },
      skip: !props.districtId,
    };
  },
});

const withComplete = graphql(CompleteSubmissionItemMutation, {
  name: 'completeItem',
});

const withReject = graphql(RejectSubmissionItemMutation, {
  name: 'rejectItem',
});

const withAddManual = graphql(AddManualSubmissionItemMutation, {
  name: 'addManualItem',
});

const withTypeChange = graphql(ChangeSubmissionItemTypeMutation, {
  name: 'changeItemType',
});

const withSplit = graphql(SplitSubmissionItemMutation, {
  name: 'splitSubmission',
  options: (props) => {
    return {
      refetchQueries: [{
        query: UnprocessedSubmissionItemsQuery,
        variables: {
          debugSubmissionState: 'unprocessed',
          districtId: props.districtId,
        },
      }],
    };
  },
});

const withInitPage = lifecycle({
  componentWillMount() {
    const {
      defaultUserDistrictId,
      setDistrictIdAction,
    } = this.props;

    setDistrictIdAction(defaultUserDistrictId);
  },
});

const mapStateToProps = (state) => {
  return {
    defaultUserDistrictId: getUserDefaultDistrictId(state),
    submissionPhoneNumber: getSubmissionPhoneNumber(state),
    submissionEmailAddress: getSubmissionEmailAddress(state),
    districtId: getDistrictId(state),
  };
};

const mapDispatchToProps = {
  setDistrictIdAction: setDistrictId,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

const enhance = compose(
  withRedux,
  withData,
  withComplete,
  withReject,
  withTypeChange,
  withAddManual,
  withSplit,
  withInitPage,
);

export default enhance(TimesheetSubmissionProcessor);
